import { AlertV2, Button, CurrencyInput, Input } from '@cimplico/components';
import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useFormContext, Controller } from 'react-hook-form';
import ProcedureTemplateSelect from '../../components/procedure-template-select/ProcedureTemplateSelect';
import ChecklistSelect from '../../components/checklist-select/ChecklistSelect';
import AutoHideOn from '../../components/AutoHideOn';

type NewWorkpaperStepTwoProps = {
    onNext: () => void;
    onPrev: () => void;
};

function NewWorkpaperStepTwo({ onNext, onPrev }: NewWorkpaperStepTwoProps): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const {
        watch,
        getValues,
        handleSubmit,
        trigger,
        formState: { errors },
        control,
    } = useFormContext();

    const entity = watch('entity');
    const periodEnd = watch('periodEnd');
    const [params] = useSearchParams();
    const returnTo = params.get('returnTo');

    function handleNext(): void {
        onNext();
    }

    function handleCancel(): void {
        navigate(returnTo || `/${shortId}/workpapers`);
    }

    return (
        <form className="flex flex-row" onSubmit={handleSubmit(handleNext)}>
            <div className="mr-4">
                <Button leftIcon={<ChevronLeftIcon />} onClick={onPrev} size="md" type="hollow" />
            </div>
            <div className="flex flex-col gap-8 w-full">
                <p className="text-2xl">{entity.name}</p>

                {/* WORKPAPER PERIOD */}
                <Controller
                    name="periodEnd"
                    control={control}
                    rules={{
                        required: 'A period end must be specified.',
                        validate: (value) => {
                            const priorWorkpaperPeriodEnd = getValues('priorWorkpaperPeriodEnd');
                            if (priorWorkpaperPeriodEnd && new Date(priorWorkpaperPeriodEnd) > new Date(value)) {
                                return 'Date must be after the period end of the carried forward workpaper';
                            }
                            return true;
                        },
                    }}
                    render={({ field: { onBlur, onChange, value } }): JSX.Element => (
                        <Input
                            label="Workpaper Period End Date"
                            type="date"
                            placeholder="Period end date..."
                            isError={Boolean(errors.periodEnd)}
                            errorMessage={errors.periodEnd?.message?.toString()}
                            onChange={(e): void => {
                                onChange(e.target.value);
                                // setMinDate(e.target.value < currentDate ? currentDate : e.target.value);
                            }}
                            onBlur={onBlur}
                            defaultValue={value ? new Date(value).toISOString().substring(0, 10) : ''}
                        />
                    )}
                />

                {/* WORKPAPER DUE DATE */}
                <Controller
                    name="dueDate"
                    control={control}
                    rules={{ required: 'A due date must be specified.' }}
                    render={({ field: { onBlur, onChange, value } }): JSX.Element => (
                        <Input
                            min={periodEnd}
                            label="Workpaper Due Date"
                            type="date"
                            placeholder="Due date..."
                            isError={Boolean(errors.dueDate)}
                            errorMessage={errors.dueDate?.message?.toString()}
                            onChange={(e): void => onChange(e.target.value)}
                            onBlur={onBlur}
                            defaultValue={value ? new Date(value).toISOString().substring(0, 10) : ''}
                        />
                    )}
                />
                {/* WORKPAPER NAME */}
                <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'A name is required.' }}
                    render={({ field }): JSX.Element => (
                        <Input
                            label="Workpaper Name"
                            type="text"
                            placeholder="Name for the workpaper..."
                            isError={Boolean(errors.name)}
                            errorMessage={errors.name?.message?.toString()}
                            onChange={(e): void => {
                                field.onChange(e.target.value);
                                trigger('name');
                            }}
                            defaultValue={field.value}
                        />
                    )}
                />

                {/* SELECT PROCEDURE TEMPLATE */}
                <Controller
                    name="procedureTemplateId"
                    rules={{ required: 'A procedure template is required.' }}
                    control={control}
                    render={({ field: { ref, onChange, value } }): JSX.Element => (
                        <ProcedureTemplateSelect
                            innerRef={ref}
                            onChange={(val): void => {
                                onChange(val);
                                trigger('procedureTemplateId');
                            }}
                            defaultTemplate={value}
                            title="Procedure Template"
                            errorMessage={errors?.procedureTemplateId?.message?.toString()}
                        />
                    )}
                />

                {/* SELECT CHECKLIST */}
                <Controller
                    name="checklistId"
                    rules={{ required: 'A checklist is required.' }}
                    control={control}
                    render={({ field: { ref, onChange, value } }): JSX.Element => (
                        <ChecklistSelect
                            innerRef={ref}
                            onChange={(val): void => {
                                onChange(val);
                                trigger('checklistId');
                            }}
                            defaultChecklist={value}
                            title="Checklist"
                            errorMessage={errors?.checklistId?.message?.toString()}
                        />
                    )}
                />
                {/* Auto hide this banner on the 3rd of March 2025. Jira task WP-1802 has been created to permanently remove this */}
                <AutoHideOn hideOn={new Date('03-03-2025')}>
                    <AlertV2
                        className="mb-4"
                        type="info"
                        title="Important changes to calculations"
                        description="We've updated how calculations are added to workpapers. Instead of adding them during creation, you'll need to add them later from a template in Other Schedules."
                    />
                </AutoHideOn>

                <Controller
                    name="materialityLimit"
                    control={control}
                    render={({ field }): JSX.Element => (
                        <CurrencyInput
                            label="Materiality Limit"
                            placeholder="Amount for auto-exclusion..."
                            onChange={(e): void => {
                                field.onChange(+e.target.value);
                                trigger('materialityLimit');
                            }}
                            defaultValue={field.value}
                            isOptional
                            textAlign="left"
                            hint="* Worksheets with checklist items won't be excluded."
                        >
                            <Controller
                                name="materialityLimitType"
                                control={control}
                                render={({ field }): JSX.Element => (
                                    <Input.Dropdown
                                        onChange={(e): void => {
                                            field.onChange(e.target.value);
                                            trigger('materialityLimitType');
                                        }}
                                        defaultValue={field.value}
                                        name="materialityLimitType"
                                        options={[
                                            { label: '$', value: 'monetary' },
                                            { label: '%', value: 'percentage' },
                                        ]}
                                    />
                                )}
                            />
                        </CurrencyInput>
                    )}
                />

                {/* FORM CANCEL + NEXT STEP */}
                <div className="flex justify-end gap-4">
                    <Button type="hollow" label="Cancel" onClick={handleCancel} />
                    <Button type="primary" isSubmit label="Next" />
                </div>
            </div>
        </form>
    );
}

export default NewWorkpaperStepTwo;
